"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SummaryTablePresenterImpl = void 0;
const vue_1 = require("vue");
const common_1 = require("../common/common");
const common_2 = require("../js/common");
const tableEditMode_1 = require("../summaryTable/tableEditMode");
const vue_sonner_1 = require("vue-sonner");
const UnlockIcon_vue_1 = __importDefault(require("../vue/icons/UnlockIcon.vue"));
const LockIcon_vue_1 = __importDefault(require("../vue/icons/LockIcon.vue"));
const MAX_USER_ROWS_WITHOUT_DYNAMIC_RENDER = 50;
class SummaryTablePresenterImpl {
    viewModel;
    constructor() { }
    setViewModel(vm) {
        this.viewModel = vm;
    }
    initEmployeeStats(branchSettings, lockStatsColumnViews, showUserAlertBadges, roles) {
        if (!this.viewModel) {
            return;
        }
        const statsSettings = {
            roleCounts: [],
            showEmployeeWorkCountCell: branchSettings.isWorkCountInSummaryPage,
            showEmployeeVacationCountCell: branchSettings.isVacationCountInSummaryPage,
            showEmployeePTOCountCell: branchSettings.isPTOCountInSummaryPage,
            showEmployeeLaborCountCell: branchSettings.isLaborInSummaryPage,
            showStatsLock: lockStatsColumnViews,
            showUserAlertBadges,
        };
        roles.forEach(r => {
            statsSettings.roleCounts.push({
                id: r.getOriginalId(),
            });
        });
        this.viewModel.stats = statsSettings;
    }
    initDates(dates) {
        if (!this.viewModel) {
            return;
        }
        this.viewModel.oplusTimeDateStrings = dates.map(d => (0, common_1.yearMonthDateString)((0, common_1.toOplusTime)(d.getDate()), 'UTC'));
    }
    initEmployeeRows(userRows, showMemo, path, branchId) {
        if (!this.viewModel) {
            return;
        }
        this.viewModel.showMemoSummary = showMemo;
        if (userRows.length > MAX_USER_ROWS_WITHOUT_DYNAMIC_RENDER) {
            // Only render user rows that are in scrolled view.
            this.viewModel.tryUseDynamicRendering = true;
        }
        const rows = [];
        // For now, expect the employee rows have already been sorted.
        userRows.forEach(dashView => {
            rows.push(this.dashViewToViewModel(dashView, showMemo, path, branchId));
        });
        this.viewModel.employeeRows = rows;
    }
    initScheduleIntervals(startDate, endDate, branchSettings) {
        if (!this.viewModel) {
            return;
        }
        this.viewModel.scheduleIntervals = [];
        if (!branchSettings.scheduleEnabled) {
            return;
        }
        const today = new Date();
        const todayUtc = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), 0, 0, 0, 0));
        // Today's interval and the one after it stay locked; everything past that unlocks by default.
        // Computed from todayUtc directly (not by searching the generated array) since the requested
        // startDate/endDate range may not include today at all (e.g. a single-day interval elsewhere).
        let unlockCutoff;
        // split by custom interval
        if (branchSettings.scheduleCustomEnabled) {
            const intervalLengthDays = branchSettings.scheduleLength;
            if (intervalLengthDays <= 0) {
                return;
            }
            const intervalLengthMs = intervalLengthDays * common_1.DAY_MS;
            const anchor = new Date(branchSettings.scheduleStartDate); // this is already in oplus time
            // Jump directly to the interval containing startDate instead of stepping one interval at a time,
            // since scheduleStartDate can be arbitrarily far in the past (or future) relative to startDate.
            const intervalsSinceAnchor = Math.floor((startDate.getTime() - anchor.getTime()) / intervalLengthMs);
            let intervalStart = new Date(anchor);
            intervalStart.setDate(intervalStart.getDate() + intervalsSinceAnchor * intervalLengthDays);
            let intervalEnd = new Date(intervalStart);
            intervalEnd.setDate(intervalEnd.getDate() + intervalLengthDays);
            // The ms-based estimate above can land one interval off around DST transitions; correct by at most one step.
            while (intervalEnd <= startDate) {
                intervalStart = intervalEnd;
                intervalEnd = new Date(intervalStart);
                intervalEnd.setDate(intervalEnd.getDate() + intervalLengthDays);
            }
            while (intervalStart > startDate) {
                intervalEnd = intervalStart;
                intervalStart = new Date(intervalStart);
                intervalStart.setDate(intervalStart.getDate() - intervalLengthDays);
            }
            // The grid arithmetic above can extrapolate backwards past anchor itself (e.g. when the
            // requested range starts before the schedule's actual start date). There are no intervals
            // before anchor, so clamp to it rather than inventing one.
            if (intervalStart < anchor) {
                intervalStart = new Date(anchor);
                intervalEnd = new Date(intervalStart);
                intervalEnd.setDate(intervalEnd.getDate() + intervalLengthDays);
            }
            while (intervalStart <= endDate) {
                const inclusiveEnd = new Date(intervalEnd);
                inclusiveEnd.setDate(inclusiveEnd.getDate() - 1);
                this.viewModel.scheduleIntervals.push({
                    startDate: new Date(intervalStart),
                    endDate: inclusiveEnd,
                    isLocked: true,
                });
                intervalStart = intervalEnd;
                intervalEnd = new Date(intervalStart);
                intervalEnd.setDate(intervalEnd.getDate() + intervalLengthDays);
            }
            // Find today's interval the same way, independent of the requested startDate/endDate range,
            // then unlock everything starting after the interval immediately following it.
            const todayIntervalsSinceAnchor = Math.floor((todayUtc.getTime() - anchor.getTime()) / intervalLengthMs);
            let todayIntervalStart = new Date(anchor);
            todayIntervalStart.setDate(todayIntervalStart.getDate() + todayIntervalsSinceAnchor * intervalLengthDays);
            let todayIntervalEnd = new Date(todayIntervalStart);
            todayIntervalEnd.setDate(todayIntervalEnd.getDate() + intervalLengthDays);
            while (todayIntervalEnd <= todayUtc) {
                todayIntervalStart = todayIntervalEnd;
                todayIntervalEnd = new Date(todayIntervalStart);
                todayIntervalEnd.setDate(todayIntervalEnd.getDate() + intervalLengthDays);
            }
            while (todayIntervalStart > todayUtc) {
                todayIntervalEnd = todayIntervalStart;
                todayIntervalStart = new Date(todayIntervalStart);
                todayIntervalStart.setDate(todayIntervalStart.getDate() - intervalLengthDays);
            }
            // step forward to the inclusive end of the interval immediately after today's
            unlockCutoff = new Date(todayIntervalEnd);
            unlockCutoff.setDate(unlockCutoff.getDate() + intervalLengthDays - 1);
        }
        else { // split by month
            let current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
            const last = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
            while (current <= last) {
                const year = current.getFullYear();
                const month = current.getMonth();
                // oplus time is UTC+0 in the date obj, so build the interval dates directly in UTC
                // instead of building local dates and adjusting after the fact.
                const intervalStart = new Date(Date.UTC(year, month, 1));
                const intervalEnd = new Date(Date.UTC(year, month + 1, 0)); // day 0 = last day of `month`
                this.viewModel.scheduleIntervals.push({
                    startDate: intervalStart,
                    endDate: intervalEnd,
                    isLocked: true,
                });
                current = new Date(year, month + 1, 1);
            }
            // Unlock everything starting after the end of the month immediately following today's month.
            unlockCutoff = new Date(Date.UTC(todayUtc.getUTCFullYear(), todayUtc.getUTCMonth() + 2, 0));
        }
        this.viewModel.scheduleIntervals.forEach(interval => {
            interval.isLocked = interval.startDate <= unlockCutoff;
        });
    }
    toggleScheduleIntervalLock(intervalIndex) {
        if (!this.viewModel) {
            return;
        }
        const interval = this.viewModel.scheduleIntervals[intervalIndex];
        if (!interval) {
            return;
        }
        interval.isLocked = !interval.isLocked;
        if (interval.isLocked) {
            vue_sonner_1.toast.info("ロックしました", { icon: (0, vue_1.h)(LockIcon_vue_1.default) });
            // de-select cells that are locked
            const dates = (0, tableEditMode_1.makeSequentialDates)(interval.startDate, interval.endDate);
            for (const emp of this.viewModel.employeeRows) {
                for (const date of dates) {
                    (0, tableEditMode_1.removeCellData)(String(emp.userId), date);
                }
            }
            document.getElementById("template-section")?.classList.remove("active");
        }
        else {
            vue_sonner_1.toast.error("ロックを解除しました", { icon: (0, vue_1.h)(UnlockIcon_vue_1.default) });
        }
    }
    /**
     * @param dateStr date string in yyyy-MM-dd format
     * @param endDateStr optional end date string in yyyy-MM-dd format, for slots that span
     * multiple days (e.g. an overnight shift). When provided, the schedule interval covering
     * the end date is also unlocked if it differs from the one covering the start date.
     */
    unlockScheduleIntervalByDate(dateStr, endDateStr) {
        if (!this.viewModel) {
            return false;
        }
        const intervals = new Set([dateStr, endDateStr]
            .filter((d) => Boolean(d))
            .map(d => this.findScheduleInterval(d))
            .filter((interval) => interval !== undefined));
        if (intervals.size === 0) {
            return false;
        }
        intervals.forEach(interval => interval.isLocked = false);
        vue_sonner_1.toast.error("ロックを解除しました", { icon: (0, vue_1.h)(UnlockIcon_vue_1.default) });
        return true;
    }
    findScheduleInterval(dateStr) {
        if (!this.viewModel) {
            return undefined;
        }
        try {
            const date = (0, common_1.dateFromString)(dateStr, "utc");
            return this.viewModel.scheduleIntervals.find(interval => date >= interval.startDate && date <= interval.endDate);
        }
        catch {
            return undefined;
        }
    }
    dashViewToViewModel(u, showMemo, path, activeBranchId) {
        return {
            userId: u.userId,
            name: u.name || u.userId.toString(),
            link: `${path}/user/${u.userId}?c=${activeBranchId}`,
            tags: u.tags.map(t => t.string),
            isBranchOwner: u.isBranchOwner || false,
            isFake: u.isFake,
            isHelp: u.isHelp,
            isDeleted: u.isDeleted,
            showMemoSummary: showMemo,
            memo: u.memo,
            weeklyLimit: u.weeklyLimit,
            imageURL: u.imageURL || "",
        };
    }
    handleCellHover(userId, date) {
        if (!this.viewModel) {
            return;
        }
        this.viewModel.hoveredUserId = userId;
        this.viewModel.hoveredDate = date;
    }
    handleCellHoverEnd() {
        if (!this.viewModel) {
            return;
        }
        this.viewModel.hoveredUserId = 0;
        this.viewModel.hoveredDate = "";
    }
    /**
     *
     * @param dateStr date string in yyyy-MM-dd format
     * @param endDateStr optional end date string in yyyy-MM-dd format, for slots that span
     * multiple days (e.g. an overnight shift). When provided, the date is treated as locked
     * if either the start or end date falls within a locked schedule interval.
     * @returns true if this date is locked by configured schedule
     */
    isLockedBySchedule(dateStr, endDateStr) {
        if (!this.viewModel) {
            return false;
        }
        if (this.isDateLockedBySchedule(dateStr)) {
            return true;
        }
        if (endDateStr && endDateStr !== dateStr) {
            return this.isDateLockedBySchedule(endDateStr);
        }
        return false;
    }
    isDateLockedBySchedule(dateStr) {
        if (!this.viewModel) {
            return false;
        }
        try {
            const date = (0, common_1.dateFromString)(dateStr, "utc");
            for (const interval of this.viewModel.scheduleIntervals) {
                if (date >= interval.startDate && date <= interval.endDate) {
                    return interval.isLocked;
                }
            }
        }
        catch {
            return false;
        }
        return false;
    }
    /**
     * @returns a list of dates that are locked in yyyy-MM-dd format
     */
    getScheduleLockedDates() {
        // use set here just in case there are duplicates
        const out = new Set();
        if (!this.viewModel) {
            return [];
        }
        for (const interval of this.viewModel.scheduleIntervals) {
            if (!interval.isLocked) {
                continue;
            }
            let start = new Date(interval.startDate);
            while (start <= interval.endDate) {
                out.add((0, common_2.goDateString)(start));
                start.setUTCDate(start.getUTCDate() + 1);
            }
        }
        return [...out];
    }
    promptUnlock(dateStr, endDateStr, onConfirm, onCancel) {
        alertModal.confirm("保護されているスケジュールを編集しようとしています。ロックを解除しますか？", () => {
            this.unlockScheduleIntervalByDate(dateStr, endDateStr);
            onConfirm?.();
        }, () => onCancel?.());
    }
}
exports.SummaryTablePresenterImpl = SummaryTablePresenterImpl;
